import { Button, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FiEdit3, FiX } from "react-icons/fi";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { TechnicalReportListItemInterface } from "../../common/interfaces";
import CustomTooltip from "../../components/CustomTooltip";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import {
  ActionButton,
  ActionsCell,
  Container,
  SearchCard,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "../sharedTable.styled";
import {
  RoleBadge,
  SelectedBar,
  SignatureFlags
} from "./documents.styled";
import SignaturesPanel from "./SignaturesPanel";
import { listTechnicalReports } from "./signatureActions";

const PAGE_SIZE = 8;

export default function SignaturesPage() {
  const [reports, setReports] = useState<TechnicalReportListItemInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] =
    useState<TechnicalReportListItemInterface | null>(null);

  const { isUser } = useAuth();

  const containerId = "toaster-signatures-page";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTechnicalReports({
        page,
        limit: PAGE_SIZE,
        search
      });
      setReports(data.items);
      setTotalPages(data.meta?.totalPages || 1);

      // Mantém o painel aberto em sincronia com a lista recarregada.
      setSelected((current) =>
        current
          ? data.items.find(
              (r) => r.technical_report_id === current.technical_report_id
            ) ?? current
          : null
      );
    } catch (error) {
      console.error("Erro ao buscar relatórios técnicos:", error);
      handleHttpError(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>Assinaturas</Title>

      <SearchCard>
        <TextFeldClientSearch
          id="signature_search"
          label="Nº do relatório, Nº da OS ou cliente"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </SearchCard>

      {selected && (
        <>
          <SelectedBar>
            <div className="info">
              <strong>
                Relatório {selected.report_number} — OS{" "}
                {selected.order_number ?? "-"}
              </strong>
              <small>{selected.client_name ?? "Cliente não informado"}</small>
            </div>
            <Button startIcon={<FiX />} onClick={() => setSelected(null)}>
              Fechar
            </Button>
          </SelectedBar>

          <SignaturesPanel
            key={selected.technical_report_id}
            reportId={selected.technical_report_id}
            isLocked={selected.status === "COMPLETED"}
            canDelete={!isUser}
            onNotify={autoSuccess}
            onError={handleHttpError}
            onChanged={load}
          />
        </>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Relatório</th>
                <th>OS</th>
                <th>Cliente</th>
                <th>Responsável</th>
                <th>Assinaturas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.technical_report_id}>
                  <td>{report.report_number}</td>
                  <td>{report.order_number ?? "-"}</td>
                  <td>{report.client_name ?? "-"}</td>
                  <td>{report.technical_responsible_name ?? "-"}</td>
                  <td>
                    <SignatureFlags>
                      <RoleBadge $signed={report.has_technician_signature}>
                        Técnico
                      </RoleBadge>
                      <RoleBadge $signed={report.has_client_signature}>
                        Cliente
                      </RoleBadge>
                    </SignatureFlags>
                  </td>
                  <ActionsCell>
                    <CustomTooltip title="Abrir assinaturas deste relatório">
                      <ActionButton onClick={() => setSelected(report)}>
                        <FiEdit3 size={20} color="#4caf50" />
                      </ActionButton>
                    </CustomTooltip>
                  </ActionsCell>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr>
                  <td colSpan={6}>Nenhum relatório técnico encontrado.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableContainer>
      )}

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />
    </Container>
  );
}
